import { api_configuration } from "../../common";
import api_gui_model = api_configuration.libraries.gui_definition_lib.types.gui_model.alan_api;
import api_client_state = api_configuration.types.client_state.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import * as expressions from "../state/expressions";
import * as Text from "../state/Text";
import * as Number from "../state/Number";
import * as Stategroup from "../state/Stategroup";
import DestructorTree from "../DestructorTree";
import { fromClientState } from "./mapToIdPathBuilder";
import { registerIdPath, registerText, registerNumber, registerStategroup } from "../state/registerClientState";
import * as Status from "../Status";
import * as microtaskQueue from "../microtaskQueue";
import { onScalarValue } from "../../../../../stream";
import { ScalarConsumer, ScalarProvider, createVariable } from "../../../../../stream";

type Sorting = string[];

export class ExpressionProvider<Type> extends ScalarProvider<Type> {
	private _subscriber: ScalarConsumer<Type> | undefined;
	private _value: Type | undefined;
	private _destructor: DestructorTree | undefined;

	constructor(private readonly fn: (destructor: DestructorTree, callback: (value: Type) => void) => void) {
		super();
	}

	get value(): Type {
		if (this._value === undefined) {
			throw new Error("not supported");
		} else {
			return this._value;
		}
	}

	subscribe(subscriber: ScalarConsumer<Type>): typeof subscriber {
		if (this._subscriber !== undefined) throw new Error("unexpected");
		this._subscriber = subscriber;
		this._destructor = new DestructorTree();
		this.fn(this._destructor, (value) => {
			this._value = value;
			subscriber.update(value);
		});
		return subscriber;
	}

	unsubscribe(subscriber: ScalarConsumer<Type>) {
		if ((<any>global)?.alan_strict && this._subscriber !== subscriber) throw new Error("unexpected");
		this._subscriber = undefined;
		this._destructor?.destroy();
	}
}

const sortableCollections = new WeakMap<api_client_state.AlanDictionary<any, any>, ScalarProvider<Sorting>>();

export function patchCollection(collection: api_client_state.Ccollection): void {
	const ui_sort = collection.inferences.collection_type().properties.ui.properties.sort;
	switch (ui_sort.state.name) {
		case "yes": {
			const has_sorting = ui_sort.state.node;
			const col = collection.properties.entries;
			sortableCollections.set(
				col,
				createVariable(
					new ExpressionProvider((destructor, callback) =>
						subscribeToSortCriteriaForDefault(col, (e) => e.properties.node, has_sorting, destructor, callback)
					),
					false
				)
			);
			break;
		}
		case "no":
			break;
	}
}

export function patchQuery(query: api_client_state.Cyes__has_result): void {
	const view_query = query.parent.properties.definition.ref;
	const has_sorting = view_query.properties.context.switch({
		candidates: ($) => {
			const app_collection = $.properties.property.ref.inferences.reference().properties.expression.output.collection();
			switch (app_collection.properties.ui.properties.sort.state.name) {
				case "no":
					return undefined;
				case "yes":
					return app_collection.properties.ui.properties.sort.state.node;
			}
		},
		node: ($) => {
			const collection = getCollectionFromQueryPath($.properties.path);
			if (collection) {
				return collection.inferences.collection().properties.ui.properties.sort.switch({
					no: () => undefined,
					yes: ($) => $,
				});
			} else {
				return undefined;
			}
		},
	});
	if (has_sorting !== undefined) {
		const col = query.properties.entries;
		sortableCollections.set(
			col,
			createVariable(
				new ExpressionProvider((destructor, callback) =>
					subscribeToSortCriteriaForDefault(
						col,
						(entry, destructor) =>
							entry.properties.entry_status.properties.initialized.switch({
								yes: ($) => $.properties.referenced_node.output.result_node(),
								no: ($) => registerIdPath(fromClientState($.properties.referenced_node), destructor),
								unknown: () => undefined,
							}),
						has_sorting,
						destructor,
						callback
					)
				),
				false
			)
		);
	}
}

function getCollectionFromQueryPath(
	path: api_views.Cquery_path,
	collection?: api_gui_model.Ccollection__type__property
): api_gui_model.Ccollection__type__property | undefined {
	return path.properties.has_steps.switch({
		no: collection,
		yes: ($) => getCollectionFromQueryPath($.properties.tail, $.properties.collection.ref),
	});
}

export function subscribeToDefaultSorting(collection: api_client_state.AlanDictionary<any, any>):
	| undefined
	| {
			subscribe: (destructor: DestructorTree, callback: (sorting: Sorting) => void) => void;
	  } {
	const collection_with_sorting = sortableCollections.get(collection);
	if (collection_with_sorting !== undefined) {
		return {
			subscribe: (destructor, callback) => {
				const subscriber = collection_with_sorting.subscribe({ update: (sorting) => callback(sorting) });
				destructor.onDestroy(() => collection_with_sorting.unsubscribe(subscriber));
			},
		};
	} else {
		return undefined;
	}
}

function subscribeToSortCriteriaForDefault<T extends api_client_state.AlanDictionaryEntry>(
	collection: api_client_state.AlanDictionary<{ node: T; init: any }, any>,
	getEntryContext: (entry: T, destructor: DestructorTree) => api_client_state.Cnode | undefined,
	definition: api_app.Cyes__sort,
	destructor: DestructorTree,
	callback: (sorting: Sorting) => void
): void {
	const scalar_value = definition.properties.property.inferences.scalar_value();
	let value_type: "text" | "number";
	let value_map: Map<string, string | undefined> | Map<string, number | undefined>;
	const update = () => {
		if (destructor.destroyed) return;
		callback(sort(value_type, value_map, definition.properties.direction.state.name));
	};

	let on_property: (key: string, context_node: api_client_state.Cnode, destructor: DestructorTree) => void;

	switch (scalar_value.variant.name) {
		case "text": {
			const text_app = scalar_value.variant.definition;
			value_map = new Map<string, string | undefined>();
			value_type = "text";

			on_property = (key, context_node, destructor) => {
				const text = registerText(text_app, context_node, destructor);
				Text.subscribeToValue(text, destructor, (value) => {
					(<Map<string, string | undefined>>value_map).set(key, value);
					microtaskQueue.add(update);
				});
			};
			break;
		}
		case "number": {
			const number_app = scalar_value.variant.definition.cast("number property");
			value_map = new Map<string, number | undefined>();
			value_type = "number";

			on_property = (key, context_node, destructor) => {
				const number = registerNumber(number_app, context_node, destructor);
				Number.subscribeToValue(number, destructor, (value) => {
					(<Map<string, number | undefined>>value_map).set(key, value);
					microtaskQueue.add(update);
				});
			};
			break;
		}
		case "state choice": {
			const stategroup_app = scalar_value.variant.definition;
			value_map = new Map<string, string | undefined>();
			value_type = "text";

			on_property = (key, context_node, destructor) => {
				const stategroup = registerStategroup(stategroup_app, context_node, destructor);
				Stategroup.subscribeToSelectedState(stategroup, destructor, (value) => {
					(<Map<string, string | undefined>>value_map).set(key, value?.key.entry);
					microtaskQueue.add(update);
				});
			};
			break;
		}
	}

	destructor.subscribeToCollection(
		collection,
		{
			on_add: (entry, destructor) => {
				const key = entry.key_value;
				const entry_context = getEntryContext(entry, destructor);
				if (entry_context !== undefined) {
					const expression = expressions.ui_node_path(definition.properties.path)([entry_context, null]);
					let c_destructor: DestructorTree | undefined;
					onScalarValue(
						expression,
						(node) => {
							if (!Status.isResolved(node)) {
								value_map.set(key, undefined);
								microtaskQueue.add(update);
							} else {
								c_destructor?.destroy();
								c_destructor = destructor.addChild();
								on_property(key, node, destructor);
							}
						},
						(scalar, subscription) => destructor.onDestroy(() => scalar.unsubscribe(subscription))
					);
				}
			},
			on_remove: (key) => {
				value_map.delete(key);
				microtaskQueue.add(update);
			},
		},
		/*init=*/ true
	);

	destructor.onDestroy(() => {
		microtaskQueue.remove(update);
	});

	update();
}

function subscribeToModelBindingPropertyPath(
	path: api_views.Cmodel_binding_property_path,
	node: api_client_state.Cnode,
	destructor: DestructorTree,
	callback: (node: api_client_state.Cnode | undefined, destructor: DestructorTree) => void
) {
	switch (path.properties.has_steps.state.name) {
		case "no":
			callback(node, destructor);
			break;
		case "yes": {
			const $yes = path.properties.has_steps.state.node;
			switch ($yes.properties.type.state.name) {
				case "parent": {
					const parent_node = node.input.position().input.parent().cast("node");
					subscribeToModelBindingPropertyPath($yes.properties.tail, parent_node, destructor, callback);
					break;
				}
				case "group": {
					const $group = $yes.properties.type.state.node;
					destructor.subscribeToEntry(node.properties.attributes, $group.properties.group.entry, {
						on_add: (attribute, destructor) => {
							const group = attribute.properties.type.cast("property").properties.type.cast("group");
							subscribeToModelBindingPropertyPath($yes.properties.tail, group.properties.node, destructor, callback);
						},
						on_remove: () => callback(undefined, destructor),
					});
					break;
				}
				case "state group": {
					const $state = $yes.properties.type.state.node;
					destructor.subscribeToEntry(node.properties.attributes, $state.properties.state_group.entry, {
						on_add: (attribute, destructor) => {
							const state_group = attribute.properties.type.cast("property").properties.type.cast("state group");
							const state = state_group.properties.states.get($state.properties.state.entry)!;
							destructor.subscribeToStategroup(state.properties.is_selected, (destructor) => {
								switch (state.properties.is_selected.state.name) {
									case "no":
										callback(undefined, destructor);
										break;
									case "yes":
										subscribeToModelBindingPropertyPath(
											$yes.properties.tail,
											state.properties.node,
											destructor,
											callback
										);
										break;
								}
							});
						},
						on_remove: () => callback(undefined, destructor),
					});
					break;
				}
			}
			break;
		}
	}
}

export function subscribeToSortCriteriaForProperty(
	collection: api_client_state.Ccollection,
	sort_property: api_views.Csort_property,
	destructor: DestructorTree,
	direction: "ascending" | "descending",
	callback: (sorting: Sorting) => void
): void {
	let value_type: "text" | "number";
	let value_map: Map<string, string | undefined> | Map<string, number | undefined>;
	const update = () => {
		if (destructor.destroyed) return;
		callback(sort(value_type, value_map, direction));
	};
	let on_property: (key: string, context_node: api_client_state.Cnode, destructor: DestructorTree) => void;

	switch (sort_property.properties.on.state.name) {
		case "text": {
			const $ = sort_property.properties.on.state.node;
			const text_app = $.inferences.state_group().inferences.text();
			value_type = "text";
			value_map = new Map<string, string | undefined>();

			on_property = (key, context_node, destructor) => {
				const text = registerText(text_app, context_node, destructor);
				Text.subscribeToValue(text, destructor, (value) => {
					(<Map<string, string | undefined>>value_map).set(key, value);
					microtaskQueue.add(update);
				});
			};
			break;
		}
		case "number": {
			const $ = sort_property.properties.on.state.node;
			const number_app = $.inferences.state_group().inferences.number();
			value_type = "number";
			value_map = new Map<string, number | undefined>();

			on_property = (key, context_node, destructor) => {
				const number = registerNumber(number_app, context_node, destructor);
				Number.subscribeToValue(number, destructor, (value) => {
					(<Map<string, number | undefined>>value_map).set(key, value);
					microtaskQueue.add(update);
				});
			};
			break;
		}
		case "state group": {
			const $ = sort_property.properties.on.state.node;
			const stategroup_app = $.inferences.state_group().inferences.state_group();
			value_type = "text";
			value_map = new Map<string, string | undefined>();

			on_property = (key, context_node, destructor) => {
				const stategroup = registerStategroup(stategroup_app, context_node, destructor);
				Stategroup.subscribeToSelectedState(stategroup, destructor, (value) => {
					(<Map<string, string | undefined>>value_map).set(key, value?.key.entry);
					microtaskQueue.add(update);
				});
			};
			break;
		}
	}

	destructor.subscribeToCollection(
		collection.properties.entries,
		{
			on_add: (entry, destructor) => {
				const key = entry.key;
				const node = entry.properties.node;
				subscribeToModelBindingPropertyPath(
					sort_property.properties.property_path,
					node,
					destructor,
					(context_node, destructor) => {
						if (context_node === undefined) {
							value_map.set(key, undefined);
							microtaskQueue.add(update);
						} else {
							on_property(key, context_node, destructor);
						}
					}
				);
			},
			on_remove: (key) => {
				value_map.delete(key);
				microtaskQueue.add(update);
			},
		},
		/*init=*/ true
	);

	destructor.onDestroy(() => {
		microtaskQueue.remove(update);
	});
}

export function subscribToSortCriteriaForQueryProperty(
	column:
		| api_client_state.Ctext__type__columns
		| api_client_state.Cnumber__type__columns
		| api_client_state.Cstate_group__type__columns,
	destructor: DestructorTree,
	direction: "ascending" | "descending",
	callback: (sorting: Sorting) => void
): void {
	let on_change: () => void;
	const update = () => {
		if (destructor.destroyed) return;
		on_change();
	};
	let value_type: "text" | "number" = "text";

	let on_property: (key: string, entry: api_client_state.Centries__yes__has_result, destructor: DestructorTree) => void;
	const value_map = new Map<string, string | number | undefined>();

	if (column instanceof api_client_state.Ctext__type__columns) {
		on_property = (key, entry, destructor) => {
			const cell = entry.properties.cells.get(column.parent.key_value)?.properties.type.cast("text");
			if (cell !== undefined) {
				destructor.subscribeToStategroup(cell.properties.is_set, (is_set_destructor) => {
					switch (cell.properties.is_set.state.name) {
						case "no": {
							value_map.set(key, undefined);
							microtaskQueue.add(update);
							break;
						}
						case "yes": {
							const is_set = cell.properties.is_set.state.node;
							is_set_destructor.subscribeToText(
								is_set,
								"subscribe_text",
								() => {
									value_map.set(key, is_set.properties.text);
									microtaskQueue.add(update);
								},
								true
							);
							break;
						}
						default:
							unexpectedState(cell.properties.is_set.state);
					}
				});
			}
		};
	} else if (column instanceof api_client_state.Cnumber__type__columns) {
		value_type = "number";
		on_property = (key, entry, destructor) => {
			const cell = entry.properties.cells.get(column.parent.key_value)?.properties.type.cast("number");
			if (cell !== undefined) {
				destructor.subscribeToStategroup(cell.properties.is_set, (is_set_destructor) => {
					switch (cell.properties.is_set.state.name) {
						case "no": {
							value_map.set(key, undefined);
							microtaskQueue.add(update);
							break;
						}
						case "yes": {
							const is_set = cell.properties.is_set.state.node;
							is_set_destructor.subscribeToNumber(
								is_set,
								"subscribe_number",
								() => {
									value_map.set(key, is_set.properties.number.value);
									microtaskQueue.add(update);
								},
								true
							);
							break;
						}
						default:
							unexpectedState(cell.properties.is_set.state);
					}
				});
			}
		};
	} else if (column instanceof api_client_state.Cstate_group__type__columns) {
		on_property = (key, entry, destructor) => {
			const cell = entry.properties.cells.get(column.parent.key_value)?.properties.type.cast("state group");
			if (cell !== undefined) {
				destructor.subscribeToStategroup(cell.properties.is_set, (is_set_destructor) => {
					switch (cell.properties.is_set.state.name) {
						case "no": {
							value_map.set(key, undefined);
							microtaskQueue.add(update);
							break;
						}
						case "yes": {
							const is_set = cell.properties.is_set.state.node;
							is_set_destructor.subscribeToText(
								is_set,
								"subscribe_state",
								() => {
									value_map.set(key, is_set.properties.state);
									microtaskQueue.add(update);
								},
								true
							);
							break;
						}
						default:
							unexpectedState(cell.properties.is_set.state);
					}
				});
			}
		};
	}

	const query = column.parent.parent;
	destructor.subscribeToStategroup(query.properties.has_result, (has_result_destructor) => {
		switch (query.properties.has_result.state.name) {
			case "no": {
				break;
			}
			case "yes": {
				const has_result = query.properties.has_result.state.node;
				on_change = () => {
					callback(sort(value_type, value_map, direction));
				};
				has_result_destructor.subscribeToCollection(
					has_result.properties.entries,
					{
						on_add: (entry, entry_destructor) => {
							on_property(entry.key, entry, entry_destructor);
						},
						on_remove: (key) => {
							value_map.delete(key);
							microtaskQueue.add(update);
						},
					},
					true
				);
				break;
			}
			default:
				unexpectedState(query.properties.has_result.state);
		}
	});
}

export function sort(
	type: "text" | "number",
	value_map: Map<string, string | number | undefined>,
	direction: "ascending" | "descending"
): Sorting {
	let sorting: Sorting;
	switch (type) {
		case "text": {
			sorting = [...(value_map as Map<string, string | undefined>)]
				.filter((x): x is [string, string] => x[1] !== undefined)
				.sort((a, b) =>
					a[1].localeCompare(b[1], undefined, {
						numeric: true,
					})
				)
				.map((x) => x[0]);
			break;
		}
		case "number": {
			sorting = [...(value_map as Map<string, number | undefined>)]
				.filter((x) => x[1] !== undefined)
				.sort((a, b) => a[1]! - b[1]!)
				.map((x) => x[0]);
			break;
		}
	}
	if (direction === "descending") {
		sorting.reverse();
	}
	const tail = [...value_map].filter((x) => x[1] === undefined).map((x) => x[0]);
	sorting = sorting.concat(tail);
	return sorting;
}
